/**
 * Pairs the two result sets on a key field and diffs the mapped fields.
 *
 * The interesting differences between these APIs are rarely "wrong value" - they are
 * representation differences (epoch millis vs ISO string, renamed fields, values only one
 * side returns). So a plain deep-equal is not enough: values that mean the same thing but
 * are encoded differently get their own `equivalent` status, which keeps the real
 * disagreements visible.
 */

const ISO_DATE_LIKE = /^\d{4}-\d{2}-\d{2}/;
const EPOCH_LIKE = /^\d{10,13}$/;

function isAbsent(value) {
    return value === undefined || value === null || value === "";
}

/** Stable stringify so object key order never counts as a difference. */
function canonical(value) {
    if (value === undefined) {
        return "undefined";
    }
    if (value === null || typeof value !== "object") {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return "[" + value.map(canonical).join(",") + "]";
    }
    return "{" + Object.keys(value).sort()
        .map(key => JSON.stringify(key) + ":" + canonical(value[key]))
        .join(",") + "}";
}

/**
 * Jackson serialises `LocalDate`/`LocalDateTime` as an array of parts - the Web API returns
 * `documentDueDate` as `[2014, 4, 15]` where the Task API returns `"2014-04-15"`. Month is
 * 1-based. Read as UTC, which is what an ISO date string without a zone also parses as, so the
 * two compare exactly.
 */
function instantFromParts(value) {
    if (!Array.isArray(value) || value.length < 3 || value.length > 7) {
        return null;
    }
    if (!value.every(part => typeof part === "number" && Number.isFinite(part)) || value[0] < 1000) {
        return null;
    }
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return Date.UTC(year, month - 1, day, hour, minute, second);
}

/** @returns {number|null} epoch millis, or null when the value is not a timestamp */
function toInstant(value) {
    const fromParts = instantFromParts(value);
    if (fromParts != null) {
        return fromParts;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        if (value > 1e11) return value;          // millis
        if (value > 1e9) return value * 1000;    // seconds
        return null;
    }
    if (typeof value === "string") {
        if (EPOCH_LIKE.test(value)) {
            return toInstant(Number(value));
        }
        if (ISO_DATE_LIKE.test(value)) {
            const parsed = Date.parse(value);
            return Number.isNaN(parsed) ? null : parsed;
        }
    }
    return null;
}

function toNumber(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) return Number(value);
    return null;
}

/**
 * @returns {{status: string, hint?: string}} status is one of
 *   equal | equivalent | different | only-task | only-web | both-absent
 */
export function compareValues(taskValue, webValue) {
    const taskAbsent = isAbsent(taskValue);
    const webAbsent = isAbsent(webValue);
    if (taskAbsent && webAbsent) {
        return {status: "both-absent"};
    }
    if (taskAbsent) {
        return {status: "only-web"};
    }
    if (webAbsent) {
        return {status: "only-task"};
    }
    if (canonical(taskValue) === canonical(webValue)) {
        return {status: "equal"};
    }

    const taskInstant = toInstant(taskValue);
    const webInstant = toInstant(webValue);
    if (taskInstant != null && webInstant != null) {
        if (taskInstant === webInstant) {
            return {status: "equivalent", hint: "same instant, different encoding"};
        }
        const deltaMs = Math.abs(taskInstant - webInstant);
        if (deltaMs < 86_400_000) {
            return {status: "different", hint: `same day, off by ${Math.round(deltaMs / 1000)}s`};
        }
        return {status: "different", hint: `off by ${Math.round(deltaMs / 86_400_000)} day(s)`};
    }

    const taskNumber = toNumber(taskValue);
    const webNumber = toNumber(webValue);
    if (taskNumber != null && webNumber != null && taskNumber === webNumber) {
        return {status: "equivalent", hint: "same number, different type"};
    }

    if (typeof taskValue === "string" && typeof webValue === "string"
        && taskValue.trim() === webValue.trim()) {
        return {status: "equivalent", hint: "differs only in whitespace"};
    }

    if (Array.isArray(taskValue) && Array.isArray(webValue) && taskValue.length === webValue.length) {
        const sortedTask = [...taskValue].map(canonical).sort();
        const sortedWeb = [...webValue].map(canonical).sort();
        if (sortedTask.every((entry, index) => entry === sortedWeb[index])) {
            return {status: "equivalent", hint: "same values, different order"};
        }
    }

    return {status: "different"};
}

function collectKeys(items) {
    const keys = new Set();
    for (const item of items) {
        if (item != null && typeof item === "object") {
            Object.keys(item).forEach(key => keys.add(key));
        }
    }
    return keys;
}

function sampleFor(items, field) {
    for (const item of items) {
        if (!isAbsent(item?.[field])) {
            return item[field];
        }
    }
    return null;
}

/**
 * A mapping normally reads `item[mapping.task]`, but a field can also be reached with a
 * `getTask`/`getWeb` accessor - the two APIs do not always split the same information the same
 * way (the Task API nests the requester in an object, the Web API flattens it into `requester*`
 * fields). `taskRoot`/`webRoot` then say which top-level field that accessor consumed, so it is
 * not reported as unmapped.
 */
function readTask(mapping, item) {
    return mapping.getTask ? mapping.getTask(item) : item[mapping.task];
}

function readWeb(mapping, item) {
    return mapping.getWeb ? mapping.getWeb(item) : item[mapping.web];
}

/**
 * The field map for a run, derived from the data rather than written out by hand.
 *
 * Hand-listing one entry per field does not scale: `my-tasks` alone needed 49, and there are ~280
 * operations across the two APIs. The two APIs agree on most field names, so identical names pair
 * automatically and a spec only has to declare where they disagree:
 *
 *   aliases   - the same information under different names (`taskId` / `id`)
 *   derived   - one side nests what the other flattens, reached with an accessor
 *   ignore    - names that match by coincidence and must not be paired
 *   notes     - a remark to show against a pair, keyed by the Task API field name
 *
 * An explicit `fieldMap` still wins, for an operation that needs full control.
 */
export function buildFieldMap(taskItems, webItems, spec) {
    if (spec.fieldMap != null) {
        return spec.fieldMap;
    }

    const taskKeys = collectKeys(taskItems);
    const webKeys = collectKeys(webItems);
    const ignore = new Set(spec.ignore ?? []);
    const notes = spec.notes ?? {};

    const declared = [...(spec.derived ?? []), ...(spec.aliases ?? [])];
    const auto = [...taskKeys]
        .filter(name => webKeys.has(name) && !ignore.has(name))
        .sort()
        .map(name => ({task: name, web: name}));

    const seen = new Set();
    const entries = [];
    for (const entry of [...declared, ...auto]) {
        const identity = `${entry.task} ${entry.web}`;
        if (seen.has(identity)) {
            continue;
        }
        seen.add(identity);
        entries.push({...entry, note: entry.note ?? notes[entry.task]});
    }

    // the compare key first - it is what the rows are paired on
    const keyName = spec.key.task;
    return [
        ...entries.filter(entry => entry.task === keyName),
        ...entries.filter(entry => entry.task !== keyName),
    ];
}

/**
 * @param {object[]} taskItems  items returned by the Task API
 * @param {object[]} webItems   items returned by the Web API
 * @param {{key: {task: string, web: string}}} spec see buildFieldMap
 */
export function compareResults(taskItems, webItems, spec) {
    const fieldMap = buildFieldMap(taskItems, webItems, spec);
    const taskKey = spec.key.task;
    const webKey = spec.key.web;

    const taskByKey = new Map(taskItems.map(item => [String(item?.[taskKey]), item]));
    const webByKey = new Map(webItems.map(item => [String(item?.[webKey]), item]));

    const allKeys = [...new Set([...taskByKey.keys(), ...webByKey.keys()])].sort();
    const onlyTask = allKeys.filter(key => taskByKey.has(key) && !webByKey.has(key));
    const onlyWeb = allKeys.filter(key => webByKey.has(key) && !taskByKey.has(key));
    const pairedKeys = allKeys.filter(key => taskByKey.has(key) && webByKey.has(key));

    // keyed by index, not by field name: a mapping may reuse a field on either side
    const summary = fieldMap.map(mapping => ({
        taskField: mapping.task,
        webField: mapping.web,
        note: mapping.note,
        equal: 0,
        equivalent: 0,
        different: 0,
        onlyTask: 0,
        onlyWeb: 0,
        bothAbsent: 0,
        examples: [],
    }));

    const rows = pairedKeys.map(key => {
        const taskItem = taskByKey.get(key);
        const webItem = webByKey.get(key);
        const fields = fieldMap.map((mapping, index) => {
            const taskValue = readTask(mapping, taskItem);
            const webValue = readWeb(mapping, webItem);
            const {status, hint} = compareValues(taskValue, webValue);

            const bucket = summary[index];
            if (status === "equal") bucket.equal++;
            else if (status === "equivalent") bucket.equivalent++;
            else if (status === "different") bucket.different++;
            else if (status === "only-task") bucket.onlyTask++;
            else if (status === "only-web") bucket.onlyWeb++;
            else bucket.bothAbsent++;

            if (status !== "equal" && status !== "both-absent" && bucket.examples.length < 3) {
                bucket.examples.push({key, taskValue, webValue, status, hint});
            }

            return {
                taskField: mapping.task,
                webField: mapping.web,
                note: mapping.note,
                taskValue,
                webValue,
                status,
                hint,
            };
        });

        const differing = fields.filter(field => field.status === "different"
            || field.status === "only-task" || field.status === "only-web").length;
        const equivalent = fields.filter(field => field.status === "equivalent").length;

        return {key, differing, equivalent, fields};
    });

    const mappedTaskFields = new Set(fieldMap.map(mapping => mapping.taskRoot ?? mapping.task));
    const mappedWebFields = new Set(fieldMap.map(mapping => mapping.webRoot ?? mapping.web));

    const unmapped = {
        task: [...collectKeys(taskItems)].filter(field => !mappedTaskFields.has(field))
            .sort().map(field => ({field, sample: sampleFor(taskItems, field)})),
        web: [...collectKeys(webItems)].filter(field => !mappedWebFields.has(field))
            .sort().map(field => ({field, sample: sampleFor(webItems, field)})),
    };

    return {
        key: spec.key,
        counts: {
            task: taskItems.length,
            web: webItems.length,
            paired: pairedKeys.length,
            onlyTask: onlyTask.length,
            onlyWeb: onlyWeb.length,
        },
        onlyTaskKeys: onlyTask,
        onlyWebKeys: onlyWeb,
        fieldSummary: summary,
        rows,
        unmapped,
    };
}
